/*! ========================================================================
    coldest-plan-improvements.js
    Plan-of-Action items executed in one bundle:
      #15 — Low-stock scarcity badge (variants under 100 units)
      #18 — Size quick-filter chips on collection grid

    (Sticky ATC intentionally NOT included — the theme already has one.)

    Scoped by template — each module only runs where it's relevant.
    Does NOT touch engraving preview, checkout, cart drawer, or any
    existing Coldest scripts.
   ======================================================================== */

(function () {
  'use strict';

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  function isProductPage() {
    return window.location.pathname.indexOf('/products/') === 0
      || window.location.pathname.indexOf('/products/') !== -1 && !window.location.pathname.startsWith('/collections/');
  }

  function isCollectionPage() {
    return window.location.pathname.indexOf('/collections/') === 0
      && window.location.pathname.indexOf('/products/') === -1;
  }

  // -----------------------------------------------------------------
  // #15 — LOW STOCK SCARCITY BADGE (under 100 units)
  // -----------------------------------------------------------------
  // On PDP: when a variant with < 100 units is selected, show a red
  // "Only X left!" badge near the add-to-cart button.
  // On collection cards: mark products whose first-available variant
  // has < 100 units (only if we can read the inventory — Coldest's
  // theme exposes `ColdestInventory` via coldest-inventory-data.js).
  function initLowStockBadge() {
    if (isProductPage()) initLowStockPDP();
    if (isCollectionPage()) initLowStockCollection();
  }

  function initLowStockPDP() {
    // Grab variant data from the Shopify productJson script if present
    var productJsonEl = document.querySelector('[id^="ProductJson-"], [data-product-json]');
    if (!productJsonEl) return;

    var product;
    try {
      product = JSON.parse(productJsonEl.textContent.trim());
    } catch (e) { return; }

    var variants = product.variants || [];
    if (!variants.length) return;

    // Build the badge slot next to ATC
    var atcForm = document.querySelector('form[action*="/cart/add"]');
    if (!atcForm) return;
    if (atcForm.querySelector('.coldest-low-stock')) return;

    var badge = document.createElement('div');
    badge.className = 'coldest-low-stock';
    badge.setAttribute('aria-live', 'polite');
    badge.style.display = 'none';
    atcForm.insertBefore(badge, atcForm.firstChild);

    function updateBadge() {
      var variantId = getCurrentVariantId(atcForm);
      var variant = variants.find(function (v) { return String(v.id) === String(variantId); });
      if (!variant) {
        badge.style.display = 'none';
        return;
      }
      var qty = typeof variant.inventory_quantity === 'number'
        ? variant.inventory_quantity
        : null;
      // Shopify sometimes hides inventory. Fall back to checking policy.
      if (qty == null) {
        badge.style.display = 'none';
        return;
      }
      if (qty > 0 && qty < 100) {
        badge.innerHTML =
          '<span class="coldest-low-stock__icon" aria-hidden="true">\u26A0</span>' +
          '<span class="coldest-low-stock__text">Only <strong>' + qty +
          '</strong> left in stock \u2014 running low!</span>';
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    }

    updateBadge();
    // Watch for variant changes (the theme toggles [name="id"] on variant picker)
    var variantInput = atcForm.querySelector('[name="id"]');
    if (variantInput && 'MutationObserver' in window) {
      var obs = new MutationObserver(updateBadge);
      obs.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }
    atcForm.addEventListener('change', updateBadge);
  }

  function getCurrentVariantId(form) {
    var el = form.querySelector('[name="id"]');
    return el ? el.value : null;
  }

  function initLowStockCollection() {
    // The theme's coldest-inventory-data.js sets window.ColdestInventory
    // as a map of { productId: { variantId: qty } }. If it's not
    // available yet, we bail — this is a progressive enhancement.
    if (!window.ColdestInventory) return;

    var cards = document.querySelectorAll('.product-block[data-product-id], [data-product-id]');
    cards.forEach(function (card) {
      if (card.querySelector('.coldest-low-stock-card')) return;
      var pid = card.getAttribute('data-product-id');
      var variants = window.ColdestInventory[pid];
      if (!variants) return;
      // Smallest non-zero inventory across variants
      var min = null;
      Object.keys(variants).forEach(function (vid) {
        var q = variants[vid];
        if (q > 0 && (min === null || q < min)) min = q;
      });
      if (min !== null && min < 100) {
        var chip = document.createElement('span');
        chip.className = 'coldest-low-stock-card';
        chip.textContent = 'Only ' + min + ' left';
        // Insert into the card image area
        var target = card.querySelector('.product-block__image-wrapper, .product-card__media, .product-block__image');
        if (target) target.appendChild(chip);
      }
    });
  }

  // -----------------------------------------------------------------
  // #18 — SIZE QUICK-FILTER CHIPS
  // -----------------------------------------------------------------
  // Above the collection product grid, render chips: 15oz / 24oz /
  // 36oz / 46oz / 20oz / 6.7oz. Clicking filters by URL param that
  // Boost AI Filter already supports. Only runs on product-listing
  // collection pages (not /collections/list, /collections/world).
  function initSizeFilterChips() {
    // Disabled: the chips link to Boost's ?pf_opt_size, and Boost is no longer
    // installed. A chip reloaded the same unfiltered grid while
    // coldest-collection-cro.js rewrote the H1 to claim the size, and the bar
    // popped in 1s after load (layout shift above the grid).
    // cleanupOrphanedChips() still removes any stale #coldest-size-chips.
    return;
    if (!isCollectionPage()) return;
    if (document.getElementById('coldest-size-chips')) return;

    // Don't show on collections that are themselves size-scoped
    var handle = window.location.pathname.replace('/collections/', '').replace(/\/$/, '');
    var sizeScopedHandles = [
      'limitless-tumblers-15oz',
      'limitless-tumblers-24oz',
      'limitless-tumblers-36oz',
      'limitless-tumblers-46oz',
      'tumblers-20oz',
    ];
    for (var i = 0; i < sizeScopedHandles.length; i++) {
      if (handle.indexOf(sizeScopedHandles[i]) !== -1) return;
    }

    // Skip pages that already have their own grid + filter system
    if (isSpecializedGridPage()) return;

    // Find the grid container
    var grid =
      document.querySelector('.collection-grid, .product-list, [data-collection-grid]') ||
      document.querySelector('.product-block')?.parentElement;
    if (!grid) return;

    // Build the chip bar
    var chips = [
      { label: '6.7oz', value: '6.7oz' },
      { label: '15oz', value: '15oz' },
      { label: '20oz', value: '20oz' },
      { label: '24oz', value: '24oz' },
      { label: '36oz', value: '36oz' },
      { label: '46oz', value: '46oz' },
    ];

    var wrap = document.createElement('div');
    wrap.id = 'coldest-size-chips';
    wrap.setAttribute('role', 'toolbar');
    wrap.setAttribute('aria-label', 'Filter by size');

    var url = new URL(window.location.href);
    var currentSize = url.searchParams.get('pf_opt_size') ||
                      url.searchParams.get('filter.v.option.size') || '';

    chips.forEach(function (c) {
      var isActive = currentSize && currentSize.toLowerCase() === c.value.toLowerCase();
      var a = document.createElement('a');
      a.className = 'coldest-size-chip' + (isActive ? ' coldest-size-chip--active' : '');
      a.textContent = c.label;
      a.setAttribute('role', 'button');
      a.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      // Build the URL — use Boost's pf_opt_size param which is what the site uses
      var next = new URL(window.location.href);
      if (isActive) {
        next.searchParams.delete('pf_opt_size');
        next.searchParams.delete('filter.v.option.size');
      } else {
        next.searchParams.set('pf_opt_size', c.value);
      }
      a.href = next.pathname + (next.search ? next.search : '');
      wrap.appendChild(a);
    });

    // Insert above the product grid
    grid.parentElement.insertBefore(wrap, grid);
  }

  // -----------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------
  // True if the page is a specialized grid (drop/curated/mystery/lander) that
  // already provides its own filtering and shouldn't show the theme's native
  // "Most relevant" sort dropdown or our injected size chips.
  function isSpecializedGridPage() {
    return !!(
      document.getElementById('coldest-lander') ||
      document.getElementById('mystery-collection-content') ||
      document.getElementById('drop-grid') ||
      document.querySelector('.drop-grid') ||
      document.querySelector('.curated-grid') ||
      document.body.classList.contains('coldest-lander-host')
    );
  }

  // Remove orphaned chips and hide the theme's utility-bar / mobile-filter shade when on specialized grid pages.
  function cleanupOrphanedChips() {
    if (isSpecializedGridPage()) {
      var existing = document.getElementById('coldest-size-chips');
      if (existing) existing.remove();
      // Hide native theme utility-bar ("Most relevant" dropdown, etc.) — inline style overrides theme CSS
      document.querySelectorAll('.utility-bar').forEach(function(el) { el.style.display = 'none'; });
      // Hide the mobile-filter dimmer overlay that sometimes gets stuck visible
      document.querySelectorAll('.filter-shade').forEach(function(el) { el.style.display = 'none'; });
      // Hide native theme collection title (".pagetitle") — our specialized grids render their own heading
      document.querySelectorAll('.pagetitle, .collection-description').forEach(function(el) { el.style.display = 'none'; });
      // Also remove .filters-active body class which the theme adds to lock scroll when filters are open
      document.body.classList.remove('filters-active');
      document.documentElement.classList.remove('filters-active');
    }
  }

  onReady(function () {
    try { initLowStockBadge(); } catch (e) { console.warn('[coldest-plan] low-stock', e); }
    // Immediate cleanup pass for utility-bar (chips inject later)
    try { cleanupOrphanedChips(); } catch (e) {}
    // Defer chip init by 1s so drop/curated/lander scripts have a chance to render first
    setTimeout(function () {
      try { initSizeFilterChips(); } catch (e) { console.warn('[coldest-plan] size-chips', e); }
      try { cleanupOrphanedChips(); } catch (e) {}
    }, 1000);

    // Re-run after 2.5s for deferred template rendering, then cleanup
    setTimeout(function () {
      try { initLowStockBadge(); } catch (e) {}
      try { initSizeFilterChips(); } catch (e) {}
      try { cleanupOrphanedChips(); } catch (e) {}
    }, 2500);

    // Final cleanup pass at 4s in case async grids render late
    setTimeout(function () { try { cleanupOrphanedChips(); } catch (e) {} }, 4000);
  });
})();
